/**
 * Says out loud that crew mail is muted, i.e. diverted to Andrei (CAP-524).
 *
 * A crew is muted by DATA - its `emails` pointed at an internal
 * `andrei+<crew>@capehost.ai` address, as the rollback runbook does - rather
 * than by a switch ([CAP-407](https://linear.app/capehost/issue/CAP-407)). Data
 * is quiet where a flag is loud, so nothing about a running screen would
 * otherwise reveal that a crew is not being emailed. This is that reveal.
 *
 * Renders NOTHING when no crew is muted.
 */

import { TriangleAlert } from 'lucide-react'

export interface MutedCrew {
  id: string
  short_name: string
  muted: boolean
}

export interface MutedCrewsNoticeProps {
  /** `muted_crews` from GET /admin/cleaning/settings, absent on an older API. */
  mutedCrews?: { active: boolean; crews: MutedCrew[] }
}

/** "Elisa", "Elisa and Alesia", "Elisa, Alesia and Marta". */
function nameList(crews: MutedCrew[]): string {
  const names = crews.map(crew => crew.short_name)
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

export function MutedCrewsNotice({ mutedCrews }: MutedCrewsNoticeProps) {
  if (!mutedCrews?.active) return null

  const muted = mutedCrews.crews.filter(crew => crew.muted)
  const live = mutedCrews.crews.filter(crew => !crew.muted)

  return (
    <div
      className="flex items-start gap-2 rounded-card border border-amber/30 bg-warningBg p-3 text-sm text-text"
      role="status"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber" aria-hidden="true" />
      <span>
        {/* The mixed case first, because it is the one that gets misread: a
            banner saying "the crews are not being emailed" while one of them
            demonstrably is would be worse than no banner. */}
        {live.length > 0 ? (
          <>
            <strong>{nameList(muted)}</strong> {muted.length === 1 ? 'is' : 'are'} muted - those
            emails come to you instead. <strong>{nameList(live)}</strong>{' '}
            {live.length === 1 ? 'is' : 'are'} live and receiving real mail.
          </>
        ) : (
          <>
            Muted: schedules and change notices are being sent for real, but to you rather than to{' '}
            {nameList(muted)}. Nothing on this screen reaches a crew until their email address is
            changed back.
          </>
        )}
      </span>
    </div>
  )
}
